export const CPE_FEED_URL = "https://cpe.org.uk/our-latest-news-category/funding-and-reimbursement/";

export interface CpeFeedArticle {
  title: string;
  publishedAt: string;
  link?: string;
}

export interface ConcessionMonthEntry {
  month: string;
  packSize: number;
  price: number;
  previousPrice?: number;
  grantedIn: CpeFeedArticle;
  rolledOverTo?: string;
  rolledOverIn?: CpeFeedArticle;
  topUp?: { amount: number; announcedIn: CpeFeedArticle };
}

export interface ConcessionProduct {
  drug: string;
  latestMonth: string;
  drugTariffPrices: Record<number, number>;
  entries: ConcessionMonthEntry[];
}

export interface ConcessionPriceApiItem {
  prescribedItemId: string;
  productName: string;
  packSize: number | null;
  concessionMonth: string;
  concessionPrice: number;
  dtPrice: number | null;
  dtPreviousPrice: number | null;
  title: string;
  link: string;
  publishedDate: string;
  isArchived: boolean;
}

export interface ConcessionPricesApiResponse {
  latestConcessionMonth: string | null;
  items: ConcessionPriceApiItem[];
}

const ROLLED_OVER = /rolled\s*over/i;

export function buildConcessionProducts(response: ConcessionPricesApiResponse | null | undefined): Map<string, ConcessionProduct> {
  const products = new Map<string, ConcessionProduct>();
  const latestMonth = response?.latestConcessionMonth;
  const rows = Array.isArray(response?.items) ? response!.items : [];
  if (!latestMonth || rows.length === 0) return products;

  const byItem = new Map<string, ConcessionPriceApiItem[]>();
  rows.forEach(r => {
    const key = String(r?.prescribedItemId ?? "").toLowerCase();
    const packSize = Number(r?.packSize);
    if (!key || !(packSize > 0) || !r?.concessionMonth || !Number.isFinite(Number(r?.concessionPrice))) return;
    if (!byItem.has(key)) byItem.set(key, []);
    byItem.get(key)!.push(r);
  });

  byItem.forEach((itemRows, key) => {
    const pricedPacks = new Set(itemRows.filter(r => Number(r.dtPrice) > 0).map(r => Number(r.packSize)));
    const kept = pricedPacks.size > 0 ? itemRows.filter(r => pricedPacks.has(Number(r.packSize))) : itemRows;

    const entries: ConcessionMonthEntry[] = kept.map(r => ({
      month: r.concessionMonth,
      packSize: Number(r.packSize),
      price: Number(r.concessionPrice),
      grantedIn: { title: r.title, publishedAt: r.publishedDate, link: r.link || undefined },
    }));
    if (!entries.some(x => x.month === latestMonth)) return;

    entries.forEach(entry => {
      const next = entries.find(x => x.packSize === entry.packSize && x.month === shiftMonth(entry.month, 1));
      if (next && ROLLED_OVER.test(next.grantedIn.title)) {
        entry.rolledOverTo = next.month;
        entry.rolledOverIn = next.grantedIn;
      }
    });

    const drugTariffPrices: Record<number, number> = {};
    [...kept]
      .filter(r => Number(r.dtPrice) > 0)
      .sort((a, b) => a.concessionMonth.localeCompare(b.concessionMonth))
      .forEach(r => { drugTariffPrices[Number(r.packSize)] = Number(r.dtPrice); });

    const latestRow = [...kept].sort((a, b) => b.concessionMonth.localeCompare(a.concessionMonth))[0];
    products.set(key, {
      drug: latestRow?.productName ?? "",
      latestMonth,
      drugTariffPrices,
      entries,
    });
  });

  return products;
}

export function shiftMonth(month: string, delta: number): string {
  const [y, m] = month.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export function formatMonth(month: string, style: "short" | "long" = "short"): string {
  const [y, m] = month.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-GB", { month: style, year: "numeric" });
}

export function currentRun(entries: ConcessionMonthEntry[], packSize: number, latestMonth: string): { months: number; since: string } {
  const months = new Set(entries.filter(x => x.packSize === packSize).map(x => x.month));
  let cursor = latestMonth;
  let count = 0;
  while (months.has(cursor)) { count++; cursor = shiftMonth(cursor, -1); }
  return { months: count, since: shiftMonth(cursor, 1) };
}
